import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { TextField } from '@app/components/form-fields/TextField';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTable } from '@app/components/table/Table';
import useModal from '@app/hooks/useModal';
import useUserActions from '@app/hooks/users/useUserActions';
import useClientsList from '@app/hooks/users/useClients';
import type { Client, ClientListParams } from '@app/services/api.types';
import { AppConfig } from '@app/utils/app-config';
import { fn } from '@app/utils';
import { url } from '@app/utils/constants';
import { Flex, type TableColumnsType } from 'antd';
import debounce from 'lodash/debounce';
import { CgSoftwareDownload } from 'react-icons/cg';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi2';
import { MdAdd, MdSearch } from 'react-icons/md';
import { ClientAddModal } from './components/ClientAddModal';
import ClientDeleteModal from './components/ClientDeleteModal';
import { ClientEditModal } from './components/ClientEditModal';
import ClientDrawer from './components/ClientDrawer';
import useMe from '@app/hooks/auth/useMe';

export const ClientsList = () => {
    const navigate = useNavigate();
    const [selectedRecord, setSelectedRecord] = useState<Client | null>(null);
    const { data: me } = useMe();

    const [params, setParams] = useState<ClientListParams>({
        page: 1,
        per_page: AppConfig.pagination_size,
        search: undefined,
        order_by: undefined,
        order_dir: undefined,
    });

    const modalAdd = useModal<Client>({
        initialData: undefined,
    });

    const modalEdit = useModal<Client>({
        initialData: undefined,
    });

    const modalDelete = useModal<Client>({
        initialData: undefined,
    });

    const closeEditModal = () => {
        modalEdit.reset();
    };

    const closeRemoveModal = () => {
        modalDelete.reset();
    };

    const { isLoading, data: userList } = useClientsList(params);

    const { exportUsers, impersonateUser } = useUserActions();
    useEffect(() => {
        if (impersonateUser.isSuccess) {
            navigate(url.adminPortal.dashboard);
        }
    }, [impersonateUser, navigate]);
    const columns: TableColumnsType<Client> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (_, record: Client) =>
                [record.first_name, record.last_name].filter(Boolean).join(' '),
            sorter: true,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Role',
            dataIndex: 'role_name',
            key: 'role_name',
            sorter: true,
            render: (value) => value || '-',
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            sorter: false,
            width: 300,
            fixed: 'right',
            render: (_value, record) => (
                <StyledButtonGroup justify="flex-end">
                    {me?.role_id !== record.role_id && (
                        <StyledButton
                            type="primary"
                            color="blue"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                                impersonateUser.mutate({ id: record.id });
                            }}>
                            Login as Client
                        </StyledButton>
                    )}
                    <StyledButton
                        icon={<HiOutlinePencil />}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            modalEdit.open(record);
                        }}>
                        Edit
                    </StyledButton>
                    <StyledButton
                        icon={<HiOutlineTrash />}
                        color="red"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation(); // Stops the event from bubbling up to the row's onClick
                            modalDelete.open(record);
                        }}>
                        Delete
                    </StyledButton>
                </StyledButtonGroup>
            ),
        },
    ];

    const handleExport = async () => {
        exportUsers.mutate(params);
    };

    return (
        <React.Fragment>
            <PageMeta title="Clients" />

            <PageHeader
                breadcrumbItems={[
                    {
                        title: <Link to={url.adminPortal.dashboard}>Dashboard</Link>,
                    },
                    { title: 'Clients' },
                ]}>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="Clients" />
                        <PageIntro>
                            <p>Impersonate client accounts for testing and support</p>
                        </PageIntro>
                    </div>
                    <div>
                        <StyledButtonGroup>
                            <StyledButton
                                type="primary"
                                color="blue"
                                icon={<MdAdd size={20} />}
                                onClick={() => modalAdd.open()}>
                                Add New
                            </StyledButton>
                        </StyledButtonGroup>
                    </div>
                </Flex>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content>
                    <Flex align="end" className="styled-table-header">
                        <div className="form-fields-wrapper">
                            <TextField
                                placeholder="Search Clients..."
                                prefix={<MdSearch size={20} />}
                                onChange={debounce((e) => {
                                    const value = e.target.value;
                                    setParams((prev) => ({
                                        ...prev,
                                        search: value,
                                        page: 1,
                                    }));
                                }, 500)}
                            />
                        </div>
                        <div>
                            <StyledButtonGroup>
                                <StyledButton
                                    type="primary"
                                    icon={<CgSoftwareDownload size={20} />}
                                    onClick={handleExport}>
                                    Export CSV
                                </StyledButton>
                            </StyledButtonGroup>
                        </div>
                    </Flex>

                    <br />

                    <StyledTable<Client>
                        columns={columns}
                        dataSource={userList?.data || []}
                        rowKey={({ id }) => id}
                        // scroll={{ x: 860 }}
                        loading={isLoading}
                        pagination={{
                            current: params.page,
                            pageSize: params.per_page,
                            total: userList?.total || 0,
                        }}
                        onChange={(pagination, _filters, sorter) => {
                            const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                            const order_by = singleSorter?.field as string | undefined;
                            const order_dir = fn.getTableOrderDir(
                                order_by,
                                singleSorter?.order ?? undefined
                            );
                            setParams((prev) => ({
                                ...prev,
                                page: pagination.current || 1,
                                order_by:
                                    singleSorter.field === 'name'
                                        ? 'first_name'
                                        : typeof singleSorter.field === 'string'
                                          ? singleSorter.field
                                          : undefined,
                                order_dir,
                            }));
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => setSelectedRecord(record),
                            };
                        }}
                        rowClassName={() => 'is-clickable'}
                    />
                </PageLayout.Content>
            </PageLayout>

            <ClientDrawer
                size={700}
                onClose={() => setSelectedRecord(null)}
                open={Boolean(selectedRecord)}
                data={selectedRecord}
            />

            <ClientAddModal
                open={modalAdd.isOpen}
                data={modalAdd.data}
                onCancel={() => modalAdd.reset()}
                onOk={() => modalAdd.reset()}
            />

            <ClientEditModal
                open={modalEdit.isOpen}
                data={modalEdit.data}
                onCancel={closeEditModal}
                onOk={closeEditModal}
            />

            <ClientDeleteModal
                open={modalDelete.isOpen}
                data={modalDelete.data}
                onCancel={closeRemoveModal}
                onOk={closeRemoveModal}
            />
        </React.Fragment>
    );
};

export default ClientsList;
